import React from 'react';

import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { Box } from '@bb-ui/react-library/dist/components/Box';
import { Card } from '@bb-ui/react-library/dist/components/Card';
import { CardContent } from '@bb-ui/react-library/dist/components/CardContent';

import { Trans, useTranslation } from 'react-i18next';
import {
    CardHeader,
    IconButton,
    Link,
    Tooltip,
} from '@bb-ui/react-library/dist/components';
import { Information } from '@bb-ui/icons/dist/small';
import { useStyles } from './UserList.styles';
import { useLicenseManagementContext } from '../../../../contexts/LicenseManagementContext';

export const BOX_PER_ROW = 3;

export const InfoText = () => {
    const classes = useStyles();
    return (
        <Typography
            component="p"
            className={classes.description}
            data-testid="description"
        >
            {/* prettier-ignore */}
            <Trans i18nKey="settings.licenseManagement.licenseOverview.description">
                Overview of your license usage and user list. Learn more about
                <Link
                    key="2"
                    href="https://help.anthology.com/illuminate/en/get-started/core-and-premium-features.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="link-to-illuminate-editions"
                    lang="en"
                >
                    Illuminate Licenses
                </Link>
                .
            </Trans>
        </Typography>
    );
};

const InfoBox = (props: {
    className: string;
    title: string;
    tooltip: string | JSX.Element;
    infoText: string | JSX.Element;
    testId: string;
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [tooltipOpen, setTooltipOpen] = React.useState(false);

    return (
        <Box maxWidth={{ sm: 1 / BOX_PER_ROW }} className={props.className}>
            <Card data-testid="totalLicenses">
                <CardHeader
                    title={props.title}
                    titleTypographyProps={{
                        variant: 'h4',
                        component: 'span',
                    }}
                    className={classes.cardHeader}
                    action={
                        <Tooltip
                            title={props.tooltip}
                            placement="top"
                            open={tooltipOpen}
                            onOpen={() => setTooltipOpen(true)}
                            onClose={() => setTooltipOpen(false)}
                        >
                            <IconButton
                                aria-label={t(
                                    'settings.licenseManagement.licenseOverview.moreInformationAriaLabel',
                                    { title: props.title },
                                )}
                                className={classes.infoIconButton}
                                size="small"
                                onKeyDown={e => {
                                    if (e.key === 'Escape') {
                                        setTooltipOpen(false);
                                    }
                                }}
                            >
                                <Information />
                            </IconButton>
                        </Tooltip>
                    }
                />
                <CardContent collapse="top">
                    <Typography
                        variant="h3"
                        component="span"
                        data-testid={props.testId}
                    >
                        {props.infoText}
                    </Typography>
                </CardContent>
            </Card>
        </Box>
    );
};
export const UserListLicenseOverview = () => {
    const ctx = useLicenseManagementContext();

    const classes = useStyles();
    const { t } = useTranslation();

    const nonAuthorLicenseQuota: number = ctx.quotas?.reader || 0;
    const authorLicenseQuota: number = ctx.quotas?.author || 0;

    const { author: authorLicenseCount, reader: adminAndViewerLicenseCount } =
        ctx.seatsUsed || { author: 0, reader: 0 };

    const hasPremiumLicense = authorLicenseQuota >= 1;

    const licenseName = hasPremiumLicense
        ? 'Illuminate Premium'
        : 'Illuminate Core';

    return (
        <>
            <InfoText />

            <div className={classes.cardsContainer}>
                <InfoBox
                    testId="license-name"
                    className={classes.card}
                    title={t(
                        'settings.licenseManagement.licenseOverview.cards.license.title',
                    )}
                    tooltip={
                        hasPremiumLicense ? (
                            /* prettier-ignore */
                            <Trans i18nKey="settings.licenseManagement.licenseOverview.cards.license.tooltips.illuminatePremium">
                                All <span lang="en">Illuminate Core</span> capabilities plus the ability to create custom
                                reports, extra users, and access to source <span lang="en">Blackboard</span> tables.
                            </Trans>
                        ) : (
                            /* prettier-ignore */
                            <Trans i18nKey="settings.licenseManagement.licenseOverview.cards.license.tooltips.illuminateCore">
                                Pre-built reports, <span lang="en">Data Q&A</span>,
                                <span lang="en">Snowflake</span> access, and more. Custom report building is restricted.
                                Limited number of users available.
                            </Trans>
                        )
                    }
                    infoText={<span lang="en">{licenseName}</span>}
                />

                <InfoBox
                    testId="admin-viewer-license-count"
                    className={classes.card}
                    title={t(
                        'settings.licenseManagement.licenseOverview.cards.adminsAndViewers.title',
                    )}
                    tooltip={t(
                        'settings.licenseManagement.licenseOverview.cards.adminsAndViewers.tooltip',
                    )}
                    infoText={t(
                        'settings.userManagement.creditsUsage.credits',
                        {
                            remaining: adminAndViewerLicenseCount,
                            total: nonAuthorLicenseQuota,
                        },
                    )}
                />

                <InfoBox
                    testId="author-license-count"
                    className={classes.card}
                    title={t(
                        'settings.licenseManagement.licenseOverview.cards.authors.title',
                    )}
                    tooltip={
                        /* prettier-ignore */
                        <Trans i18nKey="settings.licenseManagement.licenseOverview.cards.authors.tooltip">
                            Authors can create and manage custom reports. This
                            role is exclusive to <span lang="en">Illuminate Premium</span>.
                        </Trans>
                    }
                    infoText={t(
                        'settings.userManagement.creditsUsage.credits',
                        {
                            remaining: authorLicenseCount,
                            total: authorLicenseQuota,
                        },
                    )}
                />
            </div>
        </>
    );
};
